import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Truck, ArrowRight, CheckCircle2, Clock, DollarSign, ShieldCheck, AlertTriangle,
  Building2, Hash, FileText, ClipboardList, BadgeCheck,
} from "lucide-react";
import { applyPageSeo } from "@/lib/seo";

const PAGE_URL = "https://routehaulpathways.com/how-to-get-your-own-authority";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/25b69048e4e146072a0b495d53fb5d0283b59df634497f5344077c103d1e5865.jpeg";
const TITLE = "How to Get Your Own Trucking Authority (2026 Step-by-Step Guide) | RouteHaul";
const DESCRIPTION =
  "How to get your own trucking authority in 2026: get your USDOT and MC number, file BOC-3, set up insurance, UCR, and drug & alcohol consortium. Timeline, costs, and a free step-by-step guide for owner-operators.";

const STEPS = [
  { icon: Building2, title: "1. Form your business (LLC)", text: "Set up a legal entity — most owner-operators choose an LLC for liability protection. Your legal name and address here must match every filing that follows; mismatches are the #1 cause of delays." },
  { icon: Hash, title: "2. Get your free EIN", text: "Apply for a free Employer Identification Number from the IRS. It's your business's tax ID and you'll need it for banking, authority, and insurance." },
  { icon: FileText, title: "3. Apply for your USDOT & MC number", text: "Register through the FMCSA Unified Registration System (URS). Your USDOT number (free) is usually issued within a day; your MC number — your legal for-hire interstate authority — costs $300 per authority type." },
  { icon: ClipboardList, title: "4. File your BOC-3 (process agent)", text: "A BOC-3 designates a process agent in every state to accept legal documents on your behalf. A blanket filing service handles all states for about $25–$150 and can file as soon as your MC number is assigned." },
  { icon: ShieldCheck, title: "5. Get insurance filed with FMCSA", text: "You need at least $750,000 in liability coverage, and your insurer must electronically file proof (Form BMC-91 or BMC-91X) directly with the FMCSA. Buying a policy isn't enough — the electronic filing is what activates your authority." },
  { icon: FileText, title: "6. Register for UCR (and IRP/IFTA)", text: "Unified Carrier Registration (UCR) is an annual federal requirement (2026 fees start around $46 for small fleets). If you run across state lines, you'll also need apportioned (IRP) plates and an IFTA license for fuel tax." },
  { icon: BadgeCheck, title: "7. Enroll in a drug & alcohol consortium", text: "As a CDL owner-operator you must join a Consortium/Third-Party Administrator (C/TPA) for random testing and register with the FMCSA Drug & Alcohol Clearinghouse to run an annual query on yourself." },
  { icon: CheckCircle2, title: "8. Clear the 21-day window → go Active", text: "After you apply, a mandatory 21-day public protest window runs. Once it passes and your BOC-3 + insurance filings are accepted, your status flips to Active. Do not haul freight until it shows Active." },
];

const COSTS = [
  ["MC authority application", "$300 (one-time)"],
  ["BOC-3 process agent filing", "$25 – $150"],
  ["UCR registration (small fleet)", "~$46/year"],
  ["IRP plates + IFTA (if interstate)", "$1,600 – $2,600"],
  ["Commercial insurance (year 1)", "$12,000 – $22,000"],
  ["Drug & alcohol consortium", "$50 – $150/year"],
];

const FAQS = [
  { q: "How long does it take to get your own trucking authority?", a: "Typically 5 to 7 weeks from application to Active status. The biggest fixed factor is the mandatory 21-day protest window that begins after you apply for your MC number." },
  { q: "How much does it cost to get your own authority?", a: "Filing fees generally run about $900–$1,200 (MC application, BOC-3, UCR, IRP/IFTA), plus commercial insurance which is usually the largest cost at roughly $12,000–$22,000 in the first year." },
  { q: "Do I need to own a truck before I apply?", a: "No. You can start the registration process and file for your USDOT and MC numbers before you own a truck — but you cannot legally haul freight until your authority is Active and your insurance is filed." },
  { q: "How soon will brokers work with a brand-new authority?", a: "Many large brokers require your authority to be active for 90 to 180 days before they'll book with you. Planning for this waiting period is part of building your startup cash reserves." },
  { q: "Should I set up an LLC or operate as a sole proprietor?", a: "Most owner-operators choose an LLC for personal liability protection and cleaner business banking. This is a decision worth confirming with an accountant or attorney for your situation." },
  { q: "What's the most common reason authority applications get delayed?", a: "Inconsistent information. Your legal name, business address, and EIN must be identical across your FMCSA application, BOC-3, and insurance filings, or processing stalls." },
];

export default function GetYourAuthority() {
  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "How to get your own trucking authority — RouteHaul Logistics Pathways",
    seoKey: "authority",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: "How to Get Your Own Trucking Authority (2026 Step-by-Step Guide)",
          description: DESCRIPTION,
          about: "Trucking operating authority for owner-operators",
          author: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          mainEntityOfPage: PAGE_URL,
          image: OG_IMAGE,
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  }), []);

  return (
    <div className="min-h-screen bg-white" data-testid="authority-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="authority-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="authority-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
            <Link to="/register" data-testid="authority-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-5">
              <span className="w-8 h-px bg-brand" /> Owner-Operator Startup Guide
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[1.02]">
              How to get your own trucking authority
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-700">
              A plain-English, 2026 step-by-step guide to going from company driver to running under your
              own authority — your USDOT and MC number, BOC-3, insurance, UCR, and the drug &amp; alcohol
              consortium, in the right order.
            </p>
          </motion.div>

          {/* Quick answer box (snippet bait) */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="authority-quick-answer">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Quick answer
            </div>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Getting your own authority takes about <strong>5–7 weeks</strong> and roughly
              <strong> $900–$1,200 in filing fees</strong> (plus insurance). You form an LLC, get a free EIN
              and USDOT number, apply for MC authority ($300), file a BOC-3, have your insurer file proof of
              coverage, register for UCR, and join a drug &amp; alcohol consortium. <strong>You can't haul
              freight until your authority shows "Active."</strong>
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="authority-hero-cta">
              <Button className="h-13 px-7 py-3 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group w-full sm:w-auto">
                Get the free step-by-step guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Timeline + Cost */}
      <section className="max-w-4xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 p-7">
          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-4"><Clock className="w-5 h-5 text-brand" /></div>
          <h2 className="font-heading text-xl font-black text-slate-900 mb-2">How long does it take?</h2>
          <p className="text-slate-600 leading-relaxed text-sm">
            Plan for <strong>5–7 weeks</strong>. Your USDOT number can arrive within 24 hours, but a mandatory
            <strong> 21-day protest window</strong> starts after you apply for your MC number. Use that time to
            get your BOC-3 and insurance filed so your status flips to Active the moment the window closes.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 p-7">
          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-4"><DollarSign className="w-5 h-5 text-brand" /></div>
          <h2 className="font-heading text-xl font-black text-slate-900 mb-4">What does it cost?</h2>
          <ul className="space-y-1.5">
            {COSTS.map(([label, cost]) => (
              <li key={label} className="flex items-baseline justify-between gap-3 text-sm border-b border-slate-100 pb-1.5">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold text-slate-900 whitespace-nowrap">{cost}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-3">The 8 steps to your own authority</h2>
          <p className="text-slate-600 mb-10 max-w-2xl">Follow them in order — each one sets up the next. RouteHaul's free guide walks you through all of it with checklists and reminders.</p>
          <div className="space-y-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                data-testid={`authority-step-${i}`}
                className="flex gap-5 rounded-2xl border border-slate-200 bg-white p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center shrink-0"><s.icon className="w-5 h-5 text-brand" strokeWidth={2.2} /></div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-1.5">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-7">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-600" /><h2 className="font-heading text-xl font-black text-slate-900">Mistakes that delay (or sink) new authorities</h2></div>
          <ul className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-2"><span className="text-amber-600 font-black">•</span> <span><strong>Mismatched details.</strong> Your legal name, address, and EIN must be identical on the FMCSA application, BOC-3, and insurance filings.</span></li>
            <li className="flex gap-2"><span className="text-amber-600 font-black">•</span> <span><strong>Hauling before you're Active.</strong> Running freight before your status shows Active can void insurance and trigger fines.</span></li>
            <li className="flex gap-2"><span className="text-amber-600 font-black">•</span> <span><strong>No cash reserve.</strong> Brokers often need 90–180 days before they'll book you, and pay on 30–45 day terms — budget operating capital accordingly.</span></li>
            <li className="flex gap-2"><span className="text-amber-600 font-black">•</span> <span><strong>Forgetting the Clearinghouse.</strong> You must register and run an annual query on yourself, not just join a consortium.</span></li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Want this as a guided, step-by-step checklist?</h2>
          <p className="mt-4 text-orange-50 text-lg">Create a free RouteHaul account and get the full interactive 12-Step Business Foundation Guide — with plain-English explainers, a readiness diagnostic, and a searchable glossary.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="authority-final-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 text-center mb-10">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`afaq-${i}`} data-testid={`authority-faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-heading font-bold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not legal, tax, or financial advice. Always confirm specifics with FMCSA, an attorney, or an accountant.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-much-does-it-cost-to-start-a-trucking-company" className="hover:text-slate-900">Startup Costs</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/ifta-fuel-tax-calculator" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
            <Link to="/trucking-glossary" className="hover:text-slate-900">Glossary</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
